import React, { type ComponentType, type ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { token } from '@atlaskit/tokens';
import type { FieldCategory } from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { SHOW_NO_MESSAGE } from '../../../types.tsx';

type DroppableId = string;
type Props = {
	dividerText: MessageDescriptor;
	name: FieldCategory;
};

const getDividerDroppableId = (category: FieldCategory): DroppableId =>
	`${category}-active-category-droppable-id-divider`;

export const Divider: ComponentType<Props> = ({ name, dividerText }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<Droppable droppableId={getDividerDroppableId(name)} isDropDisabled>
			{(dropProvided) => (
				<div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
					<Draggable draggableId={name} index={0}>
						{(provided) => (
							<DividerContainer
								innerRef={provided.innerRef}
								{...provided.draggableProps}
								{...provided.dragHandleProps}
							>
								<DividerLine />
								<DividerMessage>
									{dividerText ? formatMessage(dividerText) : SHOW_NO_MESSAGE}
								</DividerMessage>
								<DividerLine />
							</DividerContainer>
						)}
					</Draggable>
					{dropProvided.placeholder}
				</div>
			)}
		</Droppable>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DividerLine = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `2px dashed ${token('color.border')}`,
	flexGrow: 1,
	opacity: 0.6,
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DividerMessage = styled.strong<{
	children?: ReactNode;
}>`
	font: ${token('font.heading.xxsmall')};
	color: ${token('color.text.subtlest')};
	margin-top: 0;
	padding: ${token('space.100')} ${token('space.150')} ${token('space.100')} ${token('space.150')};
	opacity: 0.8;
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @typescript-eslint/no-explicit-any,  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DividerContainer = styled.div<Record<any, any>>`
	align-items: center;
	display: flex;
	flex-direction: row;
	flex-grow: 1;
	justify-content: center;
	padding: ${token('space.100')} ${token('space.100')} ${token('space.100')} ${token('space.100')};
	&:hover,
	&:active {
		${/* sc-sel */ DividerLine}, ${/* sc-sel */ DividerMessage} {
			opacity: 1;
		}
	}
`;
